import React, { useState, useEffect } from 'react';
import { generalApi } from '../../api/generalApi';
import { referenceAPI } from '../../api/api';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import DataTable from '../../components/ui/DataTable';
import Loading from '../../components/ui/Loading';
import { DocumentChartBarIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

const LicenseReportsPage = () => {
  const [data, setData] = useState([]);
  const [licenseTypes, setLicenseTypes] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    license_type: '',
    hotel_id: '',
    department_id: '',
    status: '',
  });

  const loadReport = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.license_type) params.license_type = filters.license_type;
      if (filters.hotel_id) params.hotel_id = filters.hotel_id;
      if (filters.department_id) params.department_id = filters.department_id;
      if (filters.status) params.status = filters.status;

      const res = await generalApi.getLicenseReports(params);
      const results = res?.data?.results ?? res?.results ?? (Array.isArray(res) ? res : []);
      setData(results);
    } catch (e) {
      console.error(e);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadTypes = async () => {
      try {
        const res = await generalApi.getLicenseTypes();
        const types = Array.isArray(res) ? res : (res?.data || res?.results || []);
        setLicenseTypes(types);
      } catch (e) {
        console.error(e);
      }
    };
    loadTypes();
  }, []);

  useEffect(() => {
    const loadHotels = async () => {
      try {
        const res = await referenceAPI.getHotels();
        const data = Array.isArray(res) ? res : (res?.data || res?.results || []);
        setHotels(data);
      } catch (e) {
        console.error(e);
      }
    };
    loadHotels();
  }, []);

  useEffect(() => {
    const loadDepts = async () => {
      if (!filters.hotel_id) {
        setDepartments([]);
        return;
      }
      try {
        const res = await referenceAPI.getDepartments(filters.hotel_id);
        const data = Array.isArray(res) ? res : (res?.data || res?.results || []);
        setDepartments(data);
      } catch (e) {
        console.error(e);
        setDepartments([]);
      }
    };
    loadDepts();
  }, [filters.hotel_id]);

  const columns = [
    { header: 'Lisans Adı', accessorKey: 'name', size: 180 },
    { header: 'Tip', accessorKey: 'license_type_name', size: 120 },
    { header: 'Otel', accessorKey: 'hotel_name', size: 120, cell: ({ getValue }) => getValue() || 'Global' },
    { header: 'Departman', accessorKey: 'department_name', size: 120, cell: ({ getValue }) => getValue() || '-' },
    { header: 'Satıcı', accessorKey: 'vendor', size: 120 },
    { header: 'Bitiş Tarihi', accessorKey: 'expiry_date', size: 110 },
    { header: 'Durum', accessorKey: 'status', size: 100 },
    { header: 'Maliyet', accessorKey: 'cost', size: 100, cell: ({ row }) => `${row.original.cost ?? '-'} ${row.original.currency || ''}` },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <DocumentChartBarIcon className="h-8 w-8 mr-2 text-highlight-500" />
            Lisans Raporları
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Tarih aralığı, tip ve otel filtreleri ile raporlama
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Filtreler</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4">
            <div>
              <Label>Başlangıç Tarihi</Label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters((p) => ({ ...p, date_from: e.target.value }))}
              />
            </div>
            <div>
              <Label>Bitiş Tarihi</Label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters((p) => ({ ...p, date_to: e.target.value }))}
              />
            </div>
            <div>
              <Label>Lisans Tipi</Label>
              <select
                value={filters.license_type}
                onChange={(e) => setFilters((p) => ({ ...p, license_type: e.target.value }))}
                className="w-full h-11 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 text-gray-900 dark:text-white"
              >
                <option value="">Tümü</option>
                {licenseTypes.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Otel</Label>
              <select
                value={filters.hotel_id}
                onChange={(e) => setFilters((p) => ({ ...p, hotel_id: e.target.value || '', department_id: '' }))}
                className="w-full h-11 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 text-gray-900 dark:text-white"
              >
                <option value="">Tümü</option>
                {hotels.map((h) => (
                  <option key={h.id} value={h.id}>{h.hotel_name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Departman</Label>
              <select
                value={filters.department_id}
                onChange={(e) => setFilters((p) => ({ ...p, department_id: e.target.value || '' }))}
                className="w-full h-11 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 text-gray-900 dark:text-white"
                disabled={!filters.hotel_id}
              >
                <option value="">Tümü</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>{d.department_name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Durum</Label>
              <select
                value={filters.status}
                onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
                className="w-full h-11 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 text-gray-900 dark:text-white"
              >
                <option value="">Tümü</option>
                <option value="active">Aktif</option>
                <option value="expired">Süresi Dolmuş</option>
                <option value="pending">Beklemede</option>
                <option value="cancelled">İptal</option>
              </select>
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={loadReport} disabled={loading}>
                {loading ? 'Yükleniyor...' : 'Raporla'}
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <Loading fullScreen={false} message="Rapor yükleniyor..." />
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Sonuçlar ({data.length} kayıt)
              </h3>
            </div>
            <DataTable data={data} columns={columns} />
          </div>
        )}
      </div>
    </div>
  );
};

export default LicenseReportsPage;
